import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import type { Role } from '../api/types'

export const ROLE_PALETTE: readonly string[] = [
  '#8B5CF6',
  '#EF4444',
  '#22D3EE',
  '#34D399',
  '#FBBF24',
  '#F472B6',
  '#60A5FA',
  '#A3E635',
]

export type RoleEditorProps = {
  roles: Role[]
  lineCountByRole: Record<string, number>
  onCreate: (name: string, color: string) => void
  onPatch: (id: string, patch: { name?: string; color?: string }) => void
  onDelete: (id: string) => void
  disabled?: boolean
}

type RoleRowProps = {
  role: Role
  lineCount: number
  onPatch: RoleEditorProps['onPatch']
  onDelete: RoleEditorProps['onDelete']
  disabled: boolean
}

function RoleRow({ role, lineCount, onPatch, onDelete, disabled }: RoleRowProps): JSX.Element {
  const [name, setName] = useState(role.name)
  const [color, setColor] = useState(role.color)

  useEffect(() => setName(role.name), [role.name])
  useEffect(() => setColor(role.color), [role.color])

  const commitName = (): void => {
    const trimmed = name.trim()
    if (!trimmed || trimmed === role.name) {
      setName(role.name)
      return
    }
    onPatch(role.id, { name: trimmed })
  }

  return (
    <div className="role-row">
      <input
        type="color"
        value={color}
        disabled={disabled}
        onChange={(e) => setColor(e.currentTarget.value)}
        onBlur={() => {
          if (color !== role.color) onPatch(role.id, { color })
        }}
        title="Цвет роли"
      />
      <input
        type="text"
        value={name}
        disabled={disabled}
        onChange={(e) => setName(e.currentTarget.value)}
        onBlur={commitName}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') setName(role.name)
        }}
      />
      <span className="faint mono" style={{ minWidth: 28, textAlign: 'right' }}>
        {lineCount}
      </span>
      <button
        type="button"
        className="ghost"
        disabled={disabled}
        title="Удалить роль (реплики останутся без роли)"
        onClick={() => {
          const question =
            lineCount > 0
              ? `Удалить роль «${role.name}»? ${lineCount} реплик останутся без роли.`
              : `Удалить роль «${role.name}»?`
          if (window.confirm(question)) onDelete(role.id)
        }}
      >
        ✕
      </button>
    </div>
  )
}

export function RoleEditor({
  roles,
  lineCountByRole,
  onCreate,
  onPatch,
  onDelete,
  disabled = false,
}: RoleEditorProps): JSX.Element {
  const [newName, setNewName] = useState('')
  const nextColor = ROLE_PALETTE[roles.length % ROLE_PALETTE.length] as string
  const [newColor, setNewColor] = useState(nextColor)

  const submit = (): void => {
    const trimmed = newName.trim()
    if (!trimmed) return
    onCreate(trimmed, newColor)
    setNewName('')
    setNewColor(ROLE_PALETTE[(roles.length + 1) % ROLE_PALETTE.length] as string)
  }

  return (
    <section className="panel">
      <header>
        <h2>Роли</h2>
        <span className="faint">{roles.length}</span>
      </header>
      <div className="stack">
        {roles.length === 0 ? (
          <p className="faint">Ролей пока нет. Добавьте хотя бы одну — реплики привязываются к ролям.</p>
        ) : (
          roles.map((role) => (
            <RoleRow
              key={role.id}
              role={role}
              lineCount={lineCountByRole[role.id] ?? 0}
              onPatch={onPatch}
              onDelete={onDelete}
              disabled={disabled}
            />
          ))
        )}
        <hr />
        <div className="role-row">
          <input
            type="color"
            value={newColor}
            onChange={(e) => setNewColor(e.currentTarget.value)}
            disabled={disabled}
            title="Цвет новой роли"
          />
          <input
            type="text"
            placeholder="Имя новой роли"
            value={newName}
            disabled={disabled}
            onChange={(e) => setNewName(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit()
            }}
          />
          <button type="button" onClick={submit} disabled={disabled || !newName.trim()}>
            Добавить
          </button>
        </div>
      </div>
    </section>
  )
}
